/**
 * Facade voor viewState-toegang met legacy-callsite-detectie.
 *
 * Achtergrond (2026-05-26 key-unification): we willen toe naar één
 * id-space waar elke viewState-entry een UUID-key heeft en `entry.ref`
 * naar de concept/relation-id wijst. Tijdens de transitie blijven veel
 * code-paden nog direct `viewState[entityId]` of `setViewElementState(
 * diagram, entityId, ...)` doen waarbij entityId de concept/relation-id
 * is — dat werkt alleen voor canonieke entries (key=entityId) en faalt
 * stilletjes voor UUID-keyed entries die via ref naar entityId wijzen.
 *
 * Deze facade lost dat op door:
 *
 *  1. **Ref-eerste lookup** — vsGet/vsSet/vsDelete vinden de entry-key
 *     via `ve.ref === entityId`-zoektocht, met fallback op direct-key-
 *     match voor backward-compat met legacy canonical entries.
 *
 *  2. **Dev-warning per callsite** — élke aanroep logt z'n stack-trace
 *     naar `useDevViewStateWarnings`. Een floating popup in dev-mode
 *     toont de unieke callsites zodat we ze iteratief naar directe
 *     UUID-keyed-toegang kunnen converteren.
 *
 *  3. **Auto-create met UUID** — vsSet maakt voor een nieuwe entry een
 *     fresh UUID-key + zet `type` + `ref`. Geen "canonical-key=entityId"-
 *     pattern meer voor nieuw aangemaakte entries via dit pad.
 *
 * Niet-doel: vervanging voor élke viewState-toegang. Code die direct
 * met een ENTRY-KEY werkt (bv. `viewState[instanceKey]` waar
 * instanceKey een React-Flow node-id is) hoeft NIET via deze facade —
 * dat is per definitie correct multi-instance-aware. Alleen lookups
 * via concept/relation-id passen door deze facade.
 */
import type { Diagram, ViewElementState } from "@ae/domain";
import { create } from "zustand";

/**
 * Vind de entry-key in een viewState voor een gegeven concept/relation-id.
 *
 * Strategie:
 *  - Sweep door alle entries: match op `ve.ref === entityId`. Eerste hit
 *    wint (typisch is er hooguit 1 canonieke entry per entityId; multi-
 *    instance entries delen dezelfde ref maar krijgen aparte sub-toegang
 *    via hun eigen UUID-key, niet via dit pad).
 *  - Fallback: directe key-match `viewState[entityId]` voor legacy
 *    canonieke entries die nog geen `ref`-veld hebben.
 *
 * Returnt `null` wanneer geen entry past.
 */
export function findEntryKeyForEntity(
  viewState: Diagram["viewState"],
  entityId: string,
): string | null {
  // **Directe sleutel eerst** (2026-09-10). Kreeg deze functie een
  // ENTRY-sleutel mee in plaats van een entity-id — wat de werkbalk doet
  // sinds bewerkingen per voorkomen gaan — dan is die sleutel het antwoord.
  // Zoeken op `ref` zou dan een ánder voorkomen van dezelfde relatie kunnen
  // opleveren, want die dragen allemaal dezelfde `ref`; welke je krijgt hing
  // af van de volgorde waarin de sleutels toevallig in het object staan.
  // Voor een entity-id verandert er niets: een canonieke entry heeft
  // `sleutel === ref`, dus die matcht hier net zo goed.
  if (viewState[entityId] !== undefined) return entityId;
  // Ref-based search — voor een entity-id waarvan het voorkomen een eigen
  // UUID-sleutel heeft.
  for (const [key, ve] of Object.entries(viewState)) {
    if (ve.ref === entityId) return key;
  }
  return null;
}

/**
 * Lees een viewState-entry op via concept/relation-id. Returnt undefined
 * wanneer geen entry voor dit id bestaat.
 */
export function vsGet(
  viewState: Diagram["viewState"],
  entityId: string,
  callsite?: string,
): ViewElementState | undefined {
  recordWarning(callsite ?? "vsGet", entityId);
  const key = findEntryKeyForEntity(viewState, entityId);
  return key !== null ? viewState[key] : undefined;
}

/**
 * Vind de entry-key voor een gegeven entityId. Returnt null wanneer
 * geen entry. Public alias voor `findEntryKeyForEntity` met logging.
 */
export function vsKey(
  viewState: Diagram["viewState"],
  entityId: string,
  callsite?: string,
): string | null {
  recordWarning(callsite ?? "vsKey", entityId);
  return findEntryKeyForEntity(viewState, entityId);
}

/**
 * Upsert helper: vindt bestaande entry op entity-id en geeft de
 * effectieve key + bestaande entry terug. Wanneer geen entry bestaat,
 * genereert een fresh UUID en returnt die als key (met undefined entry).
 *
 * Caller is verantwoordelijk voor het daadwerkelijk schrijven van de
 * entry via `store.setViewElementState(diagramId, key, value)`. We
 * doen de store-write hier NIET zelf zodat de caller controle houdt
 * over scheduling/undo/persistence.
 */
export function vsUpsertKey(
  viewState: Diagram["viewState"],
  entityId: string,
  callsite?: string,
): { key: string; existing: ViewElementState | undefined; isNew: boolean } {
  recordWarning(callsite ?? "vsUpsertKey", entityId);
  const existingKey = findEntryKeyForEntity(viewState, entityId);
  if (existingKey !== null) {
    return { key: existingKey, existing: viewState[existingKey], isNew: false };
  }
  return { key: crypto.randomUUID(), existing: undefined, isNew: true };
}

/**
 * Patch-helper voor Fase 2: garandeert dat type+ref gezet zijn op de
 * "base"-entry voor een spread. Wanneer `existing` undefined is (no-op-
 * write op een niet-bestaande entry — komt voor in property-setters die
 * defensief schrijven), bouwt 'm een minimale entry met de juiste type
 * en ref. Wanneer existing wel bestaat, returnt 'm zoals-is.
 *
 * Gebruik:
 *   const base = vsPatchEntry(vsGet(viewState, relationId), relationId, "relation");
 *   const next: ViewElementState = { ...base, lineColor: value };
 *
 * Voor entity-key-pads (relationId, conceptId direct) geldt ref=entityId.
 * Voor instance-key-pads (UUID-key) moet caller een aparte ref meegeven
 * — gebruik dan vsPatchEntryWithRef.
 */
export function vsPatchEntry(
  existing: ViewElementState | undefined,
  entityId: string,
  entityType: "object" | "relation",
): ViewElementState {
  return existing ?? { type: entityType, ref: entityId };
}

/**
 * Variant voor wanneer entry-key ≠ ref (multi-instance: UUID-key, ref
 * naar concept/relation-id). Caller bepaalt de ref expliciet.
 */
export function vsPatchEntryWithRef(
  existing: ViewElementState | undefined,
  ref: string,
  entityType: "object" | "relation",
): ViewElementState {
  return existing ?? { type: entityType, ref };
}

// ─────────────────────────── Dev-warnings ───────────────────────────

interface ViewStateWarning {
  /** Stable id per (callsite, entityId-pattern) zodat dedupe werkt. */
  id: string;
  /** Eerste 5 regels van de stack-trace. */
  stack: string;
  /** Helper-naam waarvan de aanroep komt (vsGet/vsSet/etc.). */
  helper: string;
  /** Voorbeeld-entityId waarvoor de aanroep gedaan werd. */
  exampleEntityId: string;
  /** Aantal keren dat deze callsite getriggerd is sinds page-load. */
  hitCount: number;
  /** Timestamp van eerste hit. */
  firstSeen: number;
}

interface DevWarningStore {
  warnings: ViewStateWarning[];
  /** Visibility-toggle voor de popup. Begint true in dev, false in prod. */
  popupVisible: boolean;
  record: (helper: string, stack: string, entityId: string) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
  togglePopup: () => void;
}

export const useDevViewStateWarnings = create<DevWarningStore>((set) => ({
  warnings: [],
  popupVisible: true,
  record: (helper, stack, entityId) =>
    set((s) => {
      // Dedup-key = helper + eerste-callsite-frame uit stack. Negeer
      // de top-frames (Error / vsGet / etc.) en pak de éérste user-
      // code-frame zodat dezelfde caller hetzelfde id krijgt.
      const lines = stack.split("\n").slice(0, 6);
      const userFrame =
        lines.find((l) => l.includes(".tsx") || l.includes(".ts")) ?? lines[1] ?? "";
      const id = `${helper}|${userFrame.trim()}`;
      const existing = s.warnings.find((w) => w.id === id);
      if (existing) {
        existing.hitCount += 1;
        return { warnings: [...s.warnings] };
      }
      const w: ViewStateWarning = {
        id,
        stack: lines.slice(0, 5).join("\n"),
        helper,
        exampleEntityId: entityId,
        hitCount: 1,
        firstSeen: Date.now(),
      };
      return { warnings: [...s.warnings, w] };
    }),
  dismiss: (id) => set((s) => ({ warnings: s.warnings.filter((w) => w.id !== id) })),
  dismissAll: () => set({ warnings: [] }),
  togglePopup: () => set((s) => ({ popupVisible: !s.popupVisible })),
}));

function recordWarning(helper: string, entityId: string): void {
  // Standaard UIT: de facade is operationeel-correct (ref-first lookup
  // werkt voor zowel canonical als UUID-keyed entries), dus we hoeven
  // niet meer per-callsite te markeren. Wil je 't oprapen voor een
  // verdere code-opruim-actie? Zet `VITE_AE_VIEWSTATE_WARNINGS=1` in
  // `.env.local` om de popup-meldingen weer aan te zetten.
  if (!import.meta.env.DEV) return;
  if (import.meta.env.VITE_AE_VIEWSTATE_WARNINGS !== "1") return;
  const stack = new Error().stack ?? "";
  useDevViewStateWarnings.getState().record(helper, stack, entityId);
}
